/*
 * Pehratek video library. Two jobs.
 *
 * 1. Fill in each clip's file size, which Liquid cannot read.
 * 2. Make Download actually download.
 *
 * A plain <a download> only forces a save when the file sits on the same
 * origin as the page. Theme asset URLs do; anything on cdn.shopify.com or an
 * outside host does not, so the browser plays the mp4 in a tab instead.
 *
 * Those hosts do send Access-Control-Allow-Origin, so we can pull the file
 * with fetch and hand the browser a blob to save under a real filename.
 * Anything that goes wrong (no CORS, no memory on a phone, an offline tab)
 * falls straight back to the normal link behaviour.
 */
(function () {
  'use strict';

  var BUSY = 'is-downloading';

  function sameOrigin(url) {
    try {
      return new URL(url, window.location.href).origin === window.location.origin;
    } catch (e) {
      return false;
    }
  }

  function filenameFor(link) {
    var given = link.getAttribute('data-filename');
    if (given) return given;
    try {
      var path = new URL(link.href, window.location.href).pathname;
      var last = path.split('/').pop();
      if (last) return last.split('?')[0];
    } catch (e) {
      /* fall through */
    }
    return 'video.mp4';
  }

  function restore(link, label) {
    link.classList.remove(BUSY);
    link.removeAttribute('aria-busy');
    link.textContent = label;
  }

  function onClick(event) {
    var link = event.currentTarget;
    var href = link.getAttribute('href');
    if (!href) return;

    // The browser already does the right thing for same-origin files.
    if (sameOrigin(href)) return;
    if (link.classList.contains(BUSY)) {
      event.preventDefault();
      return;
    }
    if (!window.fetch || !window.URL || !window.URL.createObjectURL) return;

    event.preventDefault();

    var label = link.textContent.trim();
    link.classList.add(BUSY);
    link.setAttribute('aria-busy', 'true');
    link.textContent = 'Preparing...';

    fetch(href, { mode: 'cors', credentials: 'omit' })
      .then(function (response) {
        if (!response.ok) throw new Error('Request failed');
        return response.blob();
      })
      .then(function (blob) {
        var objectUrl = window.URL.createObjectURL(blob);
        var temp = document.createElement('a');
        temp.href = objectUrl;
        temp.download = filenameFor(link);
        document.body.appendChild(temp);
        temp.click();
        document.body.removeChild(temp);
        // Give the save dialog a moment before the blob is released.
        window.setTimeout(function () {
          window.URL.revokeObjectURL(objectUrl);
        }, 60000);
        restore(link, label);
      })
      .catch(function () {
        // Blocked, too large, or offline: let the browser handle the URL.
        restore(link, label);
        window.open(href, '_blank', 'noopener');
      });
  }

  function bind(root) {
    var links = (root || document).querySelectorAll('a[data-phr-download]');
    for (var i = 0; i < links.length; i++) {
      if (links[i].dataset.phrBound) continue;
      links[i].dataset.phrBound = '1';
      links[i].addEventListener('click', onClick);
    }
  }

  /* ----------------------------------------------------------------------
   * File sizes.
   *
   * Liquid can read a video's poster frame and running time but not how big
   * the file is, so any tile left blank asks the server for it: a HEAD for
   * Content-Length, falling back to a one-byte ranged GET for hosts that
   * refuse HEAD. Nothing is downloaded, and a failure just leaves the line
   * as it was.
   * -------------------------------------------------------------------- */

  function humanSize(bytes) {
    if (!bytes || bytes < 1) return '';
    var mb = bytes / (1024 * 1024);
    if (mb >= 1024) return (mb / 1024).toFixed(1).replace(/\.0$/, '') + ' GB';
    if (mb >= 10) return Math.round(mb) + ' MB';
    return mb.toFixed(1).replace(/\.0$/, '') + ' MB';
  }

  function readSize(url) {
    return fetch(url, { method: 'HEAD', mode: 'cors', credentials: 'omit' })
      .then(function (response) {
        var length = response.headers.get('content-length');
        if (length) return parseInt(length, 10);
        throw new Error('No length');
      })
      .catch(function () {
        return fetch(url, {
          method: 'GET',
          mode: 'cors',
          credentials: 'omit',
          headers: { Range: 'bytes=0-0' }
        }).then(function (response) {
          var range = response.headers.get('content-range');
          if (!range) throw new Error('No range');
          return parseInt(range.split('/')[1], 10);
        });
      });
  }

  function fillSizes(root) {
    if (!window.fetch) return;
    var targets = [].slice.call(
      (root || document).querySelectorAll('[data-phr-size]')
    );
    var active = 0;

    function next() {
      if (!targets.length || active >= 4) return;
      var target = targets.shift();
      var url = target.getAttribute('data-phr-size');
      target.removeAttribute('data-phr-size');
      if (!url) {
        next();
        return;
      }
      active++;
      readSize(url)
        .then(function (bytes) {
          var text = humanSize(bytes);
          if (!text) return;
          target.textContent = text;
          var separator = target.previousElementSibling;
          if (separator && separator.classList.contains('phr-vid__sep')) {
            var length = separator.previousElementSibling;
            if (length && length.textContent.trim()) separator.hidden = false;
          }
        })
        .catch(function () {
          /* leave the line alone */
        })
        .then(function () {
          active--;
          next();
        });
      next();
    }

    next();
  }

  function start(root) {
    bind(root);
    fillSizes(root);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      start(document);
    });
  } else {
    start(document);
  }

  // Theme editor: rebind when a section is redrawn.
  document.addEventListener('shopify:section:load', function (event) {
    start(event.target);
  });
})();